import {
  AlertModal,
  Button as ConfirmationButton,
  Input,
  Select,
  Stack,
  Text,
  useConfirmation,
} from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import React from 'react';

import { CompanyPlatform } from '../../company/entities';
import { Button } from '../../components/button';
import { ConfirmButton } from '../../components/confirm_button';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import { Form as FormFactory } from '../../lib/form';
import { Platform } from '../entities';

export interface PlatformViewDataProps {
  data: {
    companyPlatform: Omit<CompanyPlatform, 'created_at' | 'created_by'> | null;
    companyPlatforms: Omit<CompanyPlatform, 'created_at' | 'created_by'>[];
    platforms: Platform[];
  };
}
export interface PlatformViewProps {
  routeProps: {
    goBack: () => void;
    company_id: string;
    platform_id: string;
  };
  isNewPlatform: boolean;
  createCompanyPlatform: (input: {
    company_id: string;
    platform_id: string;
    company_id_on_platform: string;
  }) => Promise<void>;
  updateCompanyPlatform: (input: {
    company_id: string;
    platform_id: string;
    company_id_on_platform: string;
  }) => Promise<void>;
  deleteCompanyPlatform: (input: { company_id: string; platform_id: string }) => Promise<void>;
}

const getAvailablePlatforms = (
  platforms: Platform[],
  companyPlatforms: PlatformViewDataProps['data']['companyPlatforms'],
) => {
  const companyPlatformIds = companyPlatforms.map((companyPlatform) => companyPlatform.platform_id);

  return platforms
    .filter((platform) => !companyPlatformIds.some((companyPlatformId) => companyPlatformId === platform.platform_id))
    .sort((a, b) => parseInt(a.platform_id, 10) - parseInt(b.platform_id, 10));
};

class Form extends FormFactory<{ platform_id: string; company_id_on_platform: string }> {}

export const PlatformView: React.FC<PlatformViewDataProps & PlatformViewProps> = ({
  routeProps,
  data,
  isNewPlatform,
  createCompanyPlatform,
  updateCompanyPlatform,
  deleteCompanyPlatform,
}) => {
  const companyPlatform = data.companyPlatform;

  const isNotAdmin = !useIsAdmin();
  const hasPlatformsPermission = useHasScopes([BackofficeScope.companyWritePlatforms]);
  const isPrivilegedUser = !isNotAdmin || hasPlatformsPermission;
  const availablePlatforms = getAvailablePlatforms(data.platforms, data.companyPlatforms);
  const { pending, confirm, proceed, cancel, context } = useConfirmation<{
    nextCode: string;
    platformName: string;
    previousCode: string;
  }>();

  if (!isNewPlatform && companyPlatform === null) {
    return <React.Fragment>Platform not found</React.Fragment>;
  }

  if (isNewPlatform && availablePlatforms.length === 0) {
    return (
      <React.Fragment>
        <h3>All platforms have been selected for this company</h3>
        <Button type="button" onClick={() => routeProps.goBack()}>
          Go back
        </Button>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      {companyPlatform !== null && (
        <h3>
          <span style={{ marginRight: 20 }}>{`#${companyPlatform.platform_id} ${companyPlatform.platform_name}`}</span>
          <ConfirmButton
            size="tiny"
            label="Delete"
            initialButtonColor={'red'}
            disabled={!isPrivilegedUser}
            onConfirm={async () => {
              await deleteCompanyPlatform({
                company_id: routeProps.company_id,
                platform_id: routeProps.platform_id,
              });

              routeProps.goBack();
            }}
          />
        </h3>
      )}
      <Form
        validateFields={{
          company_id_on_platform: {
            exist: 'Enter a company id on platform',
            custom: {
              error: 'The ID must be a string containing only alphanumeric characters',
              condition: (value) => {
                if (typeof value !== 'string') {
                  return false;
                }

                return value.match(/^[a-zA-Z0-9_-]+$/) !== null;
              },
            },
          },
          ...(isNewPlatform && {
            platform_id: {
              exist: 'Please select a platform',
            },
          }),
        }}
        initialValues={{
          platform_id: companyPlatform !== null ? companyPlatform.platform_id : '',
          company_id_on_platform: companyPlatform !== null ? companyPlatform.company_id_on_platform : '',
        }}
        onSubmit={async (form) => {
          const { company_id } = routeProps;
          if (isNewPlatform) {
            await createCompanyPlatform({
              company_id,
              platform_id: form.platform_id,
              company_id_on_platform: form.company_id_on_platform,
            });
            routeProps.goBack();
            return;
          }

          if (companyPlatform === null || form.company_id_on_platform === companyPlatform.company_id_on_platform) {
            return;
          }

          const confirmed = await confirm({
            previousCode: companyPlatform.company_id_on_platform,
            nextCode: form.company_id_on_platform,
            platformName: companyPlatform.platform_name,
          });

          if (!confirmed) {
            return;
          }

          await updateCompanyPlatform({
            company_id,
            platform_id: companyPlatform.platform_id,
            company_id_on_platform: form.company_id_on_platform,
          });
          routeProps.goBack();
        }}>
        {({ form, errors, onSubmit, onChange, isSubmittingForm }) => {
          const hasChangedCompanyIdOnPlatform =
            companyPlatform !== null && form.company_id_on_platform !== companyPlatform.company_id_on_platform;

          return (
            <>
              <form onSubmit={onSubmit}>
                <Stack direction="column" gap="medium">
                  {isNewPlatform && (
                    <Select
                      label={errors.platform_id ?? 'Select a platform'}
                      labelPosition="block"
                      placeholder="Platform"
                      options={availablePlatforms.map((platform) => ({
                        value: platform.platform_id,
                        label: `${platform.platform_name} (#${platform.platform_id})`,
                      }))}
                      value={form.platform_id || null}
                      onChange={(option) => {
                        if (option !== null) {
                          onChange({ name: 'platform_id', value: option.value });
                        }
                      }}
                      disabled={!isPrivilegedUser}
                    />
                  )}

                  <Input
                    label={errors.company_id_on_platform ?? 'Company ID on platform'}
                    name="company_id_on_platform"
                    placeholder="Company ID on platform"
                    value={form.company_id_on_platform}
                    onChange={({ value }) => onChange({ name: 'company_id_on_platform', value })}
                    disabled={!isPrivilegedUser}
                  />
                  <Stack direction="row" gap="small">
                    <Button
                      type="submit"
                      kind="primary"
                      loading={isSubmittingForm}
                      disabled={
                        !isPrivilegedUser || isSubmittingForm || (!isNewPlatform && !hasChangedCompanyIdOnPlatform)
                      }>
                      {isNewPlatform ? 'Create' : 'Update'}
                    </Button>
                    <Button type="button" onClick={() => routeProps.goBack()}>
                      Cancel
                    </Button>
                  </Stack>
                </Stack>
              </form>
              <AlertModal
                title="Confirm company ID on platform update"
                description={
                  <Text>
                    <Text>This changes master data for </Text>
                    <Text weight="bold">{context?.platformName ?? ''}</Text>
                    <Text>. Existing transports will not be rewritten. Future platform matching will use </Text>
                    <Text weight="bold">{context?.nextCode ?? ''}</Text>
                    <Text> instead of </Text>
                    <Text weight="bold">{context?.previousCode ?? ''}</Text>
                    <Text>.</Text>
                  </Text>
                }
                trigger={null}
                open={pending}
                onClose={cancel}>
                <ConfirmationButton kind="subtle" onClick={cancel}>
                  Cancel
                </ConfirmationButton>
                <ConfirmationButton kind="primary" onClick={proceed}>
                  Confirm update
                </ConfirmationButton>
              </AlertModal>
            </>
          );
        }}
      </Form>
    </React.Fragment>
  );
};
